/**
 * 云牛集 · 牛只交易平台 — 格式化工具
 */

/** 金额千分位 + 元 */
export function formatMoney(value: number | null | undefined, withUnit = true): string {
  const n = Number(value ?? 0);
  const formatted = n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return withUnit ? `¥${formatted}` : formatted;
}

/** 纯数字千分位 */
export function formatNumber(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString('zh-CN');
}

/** 日期 → YYYY-MM-DD */
export function formatDate(value: string | number | Date | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 日期时间 → YYYY-MM-DD HH:mm */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${formatDate(d)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`;
}

/** 体重展示 */
export function formatWeight(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toFixed(1)} kg`;
}

/**
 * 由出生日期计算日龄（天）。
 * 后端 cattle 无 age_days 字段，前端统一按 birth_date 折算补齐展示。
 * 无效日期/空值返回 undefined；未来日期按 0 天兜底。
 */
export function computeAgeDays(
  birthDate: string | number | Date | null | undefined,
): number | undefined {
  if (!birthDate) return undefined;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return undefined;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / 86400000);
}

/** 手机号脱敏 138****0000 */
export function maskPhone(value: string | null | undefined): string {
  if (!value) return '-';
  const s = value.replace(/\s/g, '');
  if (s.length < 7) return s;
  return `${s.slice(0, 3)}****${s.slice(-4)}`;
}

/** 姓名脱敏 */
export function maskName(value: string | null | undefined): string {
  if (!value) return '-';
  if (value.length <= 1) return '*';
  return `${value[0]}${'*'.repeat(value.length - 1)}`;
}

/** 相对时间：xx分钟前 / xx小时前 / xx天前 / 日期 */
export function timeAgo(value: string | number | Date | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}天前`;
  return formatDate(d);
}

/** 秒 → "MM:SS" 或 "HH:MM:SS"（倒计时展示） */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = `${m}`.padStart(2, '0');
  const ss = `${sec}`.padStart(2, '0');
  if (h > 0) {
    return `${h}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

/** 生成订单号 ORD-{日期}-{10位随机} */
export function genOrderNo(): string {
  const date = new Date();
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  const rand = `${Math.floor(Math.random() * 1e10)}`.padStart(10, '0');
  return `ORD-${y}${m}${d}-${rand}`;
}

/** 生成委托单号 BOR-{日期}-{8位随机} */
export function genBrokerOrderNo(): string {
  const date = new Date();
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  const rand = `${Math.floor(Math.random() * 1e8)}`.padStart(8, '0');
  return `BOR-${y}${m}${d}-${rand}`;
}

/** 金额千分位输入展示（编辑态） */
export function formatInputNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/** 距离展示 */
export function formatDistance(km: number | null | undefined): string {
  if (km === null || km === undefined) return '-';
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
}

/** 计算两个 GPS 点的距离（km，Haversine） */
export function haversine(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
): number {
  const R = 6371;
  const toRad = (v: number): number => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
